import { Component, type ReactNode } from "react"

// Sprint 5 — Error boundary defensivo para MaterializedMesh.
//
// useGLTF de drei lanza durante render (vía Suspense) cuando el .glb
// falla en cargar — CORS bloqueado, 404, archivo corrupto, etc. Sin
// boundary, ese throw mata el subtree del Canvas R3F y queda pantalla
// negra. Con boundary, capturamos el error y renderizamos el `fallback`
// (típicamente el icosaedro emisivo legacy) para que el avatar siga
// existiendo aunque su malla 3D no esté disponible.
//
// El boundary también captura errores síncronos de los effects que
// configuran materiales (traverse + setProperty). Errores asíncronos
// (post-mount) no llegan acá; para eso useGLTF + drei tienen su
// propio error reporting que aparece en consola.
//
// Solo loggea una vez por instancia para no saturar la consola con
// errores repetidos durante reconnect / hot reload.

interface Props {
    fallback: ReactNode
    children: ReactNode
    /** Identificador opcional para distinguir loggeos en consola. */
    label?: string
}

interface State {
    hasError: boolean
}

export class MeshErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false }

    static getDerivedStateFromError(): State {
        return { hasError: true }
    }

    componentDidCatch(error: unknown): void {
        const label = this.props.label ?? "MaterializedMesh"
        // eslint-disable-next-line no-console
        console.warn(
            `[domo] ${label} falló — fallback al icosaedro:`,
            error instanceof Error ? error.message : error
        )
    }

    render(): ReactNode {
        if (this.state.hasError) return this.props.fallback
        return this.props.children
    }
}
